/**
 * The online booking settings' limits, as the server holds them: checked
 * here first so a form says what is wrong before it is sent, never instead of the server.
 */
import type { StaffKey } from './staffText'
import type { BookingBranchSettings, BookingPolicy, BookingServiceRow, DayHours } from './types'

/** A minute of the day as HH:MM; '' for none. */
export function clockOf(min: number | null): string {
  if (min === null) return ''
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
}

/** HH:MM as a minute of the day; null for empty or not a time. */
export function minutesOf(clock: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(clock.trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

const within = (v: number, lo: number, hi: number) => Number.isInteger(v) && v >= lo && v <= hi

export function branchProblem(s: BookingBranchSettings): StaffKey | null {
  if (!within(s.daysAhead, 1, 90)) return 'booking.problem.daysAhead'
  if (!within(s.minNoticeMin, 0, 1440)) return 'booking.problem.notice'
  return hoursProblem(s.hours)
}

/** Booking hours that are not one day's ranges without overlap; null follows the opening hours. */
export function hoursProblem(hours: readonly DayHours[] | null): StaffKey | null {
  if (hours === null) return null
  if (hours.length === 0 || hours.length > 42) return 'booking.problem.hours'
  for (const h of hours) {
    if (h.day < 0 || h.day > 6 || h.open < 0 || h.close > 1440 || h.close <= h.open) return 'booking.problem.hours'
    if (hours.some((o) => o !== h && o.day === h.day && o.open < h.close && h.open < o.close)) return 'booking.problem.hours'
  }
  return null
}

/** Booking hours sorted by day, then start. */
export function sortedHours(hours: readonly DayHours[]): DayHours[] {
  return [...hours].sort((a, b) => a.day - b.day || a.open - b.open)
}

/** Another range on a day: an hour after its last, else that day's opening hours, else 09:00–17:00. */
export function addRange(hours: readonly DayHours[], day: number, opening: readonly DayHours[] = []): DayHours[] {
  const last = sortedHours(hours.filter((h) => h.day === day)).at(-1)
  if (last) {
    const open = Math.min(last.close + 60, 1380)
    return sortedHours([...hours, { day, open, close: Math.min(open + 60, 1440) }])
  }
  const own = opening.find((h) => h.day === day)
  return sortedHours([...hours, own ? { ...own } : { day, open: 9 * 60, close: 17 * 60 }])
}

/** The week booking hours start from: the opening hours, else Monday–Friday 09:00–17:00. */
export function startingHours(opening: readonly DayHours[] | undefined): DayHours[] {
  const own = (opening ?? []).map((h) => ({ ...h }))
  return own.length > 0 ? sortedHours(own) : [1, 2, 3, 4, 5].map((day) => ({ day, open: 9 * 60, close: 17 * 60 }))
}

/** One weekday switched in or out of a service's days, Sunday first. */
export function toggleWeekday(days: readonly number[], day: number): number[] {
  return days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b)
}

/** Booking on at a branch with no opening hours: the server offers no slot then
 *  (booking.Window.Grid), so customers see no free day. */
export function bookingWithoutHours(on: boolean, hours: readonly DayHours[] | undefined): boolean {
  return on && (hours ?? []).length === 0
}

export function serviceProblem(r: BookingServiceRow): StaffKey | null {
  if (!within(r.slotMin, 5, 240)) return 'booking.problem.slot'
  if (!within(r.perSlot, 1, 50)) return 'booking.problem.perSlot'
  if (r.weekdays.length === 0) return 'booking.problem.weekdays'
  if (r.dailyMax !== null && !within(r.dailyMax, 1, 5000)) return 'booking.problem.dailyMax'
  if (r.daysAhead !== null && !within(r.daysAhead, 1, 90)) return 'booking.problem.daysAhead'
  if (r.minNoticeMin !== null && !within(r.minNoticeMin, 0, 1440)) return 'booking.problem.notice'
  return null
}

/** The customer rules' and fields' limits. */
export function customerRulesProblem(
  p: Pick<BookingPolicy, 'changeUntilHours' | 'noShowLimit' | 'noShowDays' | 'noShowBlockDays' | 'fields'>,
): StaffKey | null {
  if (!within(p.changeUntilHours, 0, 168)) return 'booking.problem.changeUntil'
  if (!within(p.noShowLimit, 0, 20) || !within(p.noShowDays, 1, 365) || !within(p.noShowBlockDays, 1, 365))
    return 'booking.problem.noShow'
  if (p.fields.mobile === 'off' && p.fields.email === 'off') return 'booking.problem.contactField'
  return null
}

export function policyProblem(p: BookingPolicy): StaffKey | null {
  if (!within(p.maxOpenPerContact, 1, 10)) return 'booking.problem.maxOpen'
  if (!within(p.maxHoldsPerKey, 1, 500)) return 'booking.problem.maxHolds'
  if (p.remindBeforeMin !== null && !within(p.remindBeforeMin, 5, 1440)) return 'booking.problem.before'
  if (!within(p.onTimeMin, 0, 240)) return 'booking.problem.onTime'
  if (!within(p.checkInEarlyMin, p.onTimeMin, 240)) return 'booking.problem.checkInEarly'
  if (!within(p.lateMin, p.onTimeMin, 240)) return 'booking.problem.checkInLate'
  const rules = customerRulesProblem(p)
  if (rules) return rules
  const url = p.manageUrl.trim()
  if (url && (!url.startsWith('https://') || !url.includes('{token}') || url.length > 200)) return 'booking.problem.manageUrl'
  return null
}

/** The policy as PUT takes it: its own fields, and not what the server adds. */
export function policyToSend(p: BookingPolicy): BookingPolicy {
  const { testPageUrl: _url, testPageUntil: _until, ...rest } = p
  return { ...rest, manageUrl: rest.manageUrl.trim() }
}

/** 120 a minute trusted, 30 needing a code. */
export function defaultRate(trusted: boolean): number {
  return trusted ? 120 : 30
}

/** A key row's switch label: its state, never a fixed "On". */
export function keyStateLabel(active: boolean): StaffKey {
  return active ? 'booking.key.on' : 'booking.key.off'
}
